import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '~/components/Dialog'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  useEditEvaluationModalValues,
  useCloseEditEvaluationNameModal,
} from './state'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useUpdateEvaluationNameMutation } from '~/app/evaluations/@settings/[slug]/_components/EvaluationName'
import { EditEvaluationIcon } from './icons'

const formSchema = z.object({
  name: z
    .string()
    .min(10, 'Name must be at least 10 characters long.')
    .max(200, 'Name cannot be greater than 200 characters.'),
})

type FormSchema = z.infer<typeof formSchema>

export function EditEvaluationNameModal() {
  const { isOpen, evaluationId, evaluationVersion, evaluationName } =
    useEditEvaluationModalValues()
  const close = useCloseEditEvaluationNameModal()
  const updateNameMutation = useUpdateEvaluationNameMutation()
  const [isLoading, setIsLoading] = useState(false)

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {},
  })

  const {
    formState: { isDirty, errors },
  } = form

  useEffect(() => {
    form.reset({ name: evaluationName })
  }, [evaluationId, evaluationName])

  const handleSave = useCallback(
    async (values: FormSchema) => {
      if (!isDirty) {
        close()
        return
      }
      setIsLoading(true)
      await updateNameMutation.mutateAsync({
        name: values.name,
        evaluationId,
        evaluationVersion,
      })
      setIsLoading(false)
      close()
    },
    [isDirty, evaluationId, evaluationVersion]
  )

  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      close()
    }
  }, [])

  const isDisabled = useMemo(
    () => !!(isLoading || !isDirty || errors.name),
    [isLoading, isDirty, errors.name]
  )

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="px-5 py-5 w-[37.5rem]" placement="center">
        <DialogHeader className="flex gap-4 items-center">
          <div className="bg-[#F4F6F7] p-2 rounded-lg">
            <EditEvaluationIcon className="w-6" />
          </div>
          <DialogTitle className="text-black text-lg font-medium pr-4">
            Edit evaluation name
          </DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col h-full"
          onSubmit={form.handleSubmit(handleSave)}
        >
          <input
            type="text"
            className="w-full p-3.5 mt-4 border border-[#D6D9DC] rounded-md resize-none placeholder:text-[#A9A9A9] text-sm"
            placeholder="Enter a name for this evaluation"
            {...form.register('name')}
          />
          {errors.name ? (
            <p className="text-sm text-red-500 mt-2">{errors.name.message}</p>
          ) : null}
          <footer className="flex flex-col sm:flex-row justify-end items-stretch sm:items-center mt-10 gap-2">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <DialogClose asChild>
                <button
                  type="button"
                  onClick={() => close()}
                  className="px-7 py-3 rounded-xl border border-[#707070] text-[#707070] hover:border-gray-400 hover:text-gray-400 transition-colors font-semibold text-sm cursor-pointer"
                >
                  Cancel
                </button>
              </DialogClose>
              <button
                className="px-7 py-[0.8rem] rounded-xl text-white bg-primary-500 hover:bg-blue-500 transition-colors font-semibold text-sm cursor-pointer disabled:bg-blue-300 disabled:cursor-not-allowed"
                type="submit"
                disabled={isDisabled}
              >
                Save
              </button>
            </div>
          </footer>
        </form>
      </DialogContent>
    </Dialog>
  )
}
